import { supabase } from '../supabase';
import { getCalendarDaysPastDue } from '../invoiceDates';

export interface ArOpenInvoice {
  id: string;
  kind: 'invoice' | 'legacy_booking';
  number: string;
  bookingId: string | null;
  total: number;
  balance: number;
  dueDate: string | null;
  invoiceDate: string | null;
  daysPastDue: number;
  isOverdue: boolean;
}

export interface ArCustomerGroup {
  customerId: string | null;
  customerName: string;
  outstanding: number;
  overdue: number;
  invoiceCount: number;
  oldestDaysPastDue: number;
  invoices: ArOpenInvoice[];
}

export type AgingBucketKey = 'current' | '1_30' | '31_60' | '61_90' | '90_plus';

export interface AgingBucket {
  key: AgingBucketKey;
  label: string;
  amount: number;
  count: number;
}

export interface AccountsReceivable {
  totalOutstanding: number;
  totalOverdue: number;
  customerCount: number;
  invoiceCount: number;
  customers: ArCustomerGroup[];
  buckets: AgingBucket[];
}

const BUCKET_DEFS: { key: AgingBucketKey; label: string }[] = [
  { key: 'current', label: 'Current' },
  { key: '1_30', label: '1–30 days' },
  { key: '31_60', label: '31–60 days' },
  { key: '61_90', label: '61–90 days' },
  { key: '90_plus', label: '90+ days' },
];

function bucketFor(days: number): AgingBucketKey {
  if (days <= 0) return 'current';
  if (days <= 30) return '1_30';
  if (days <= 60) return '31_60';
  if (days <= 90) return '61_90';
  return '90_plus';
}

function principalPaid(payments: any[]): number {
  return (payments || [])
    .filter((p) => ['completed', 'succeeded', 'paid'].includes(String(p.status)))
    .reduce((s, p) => s + Math.max(0, (Number(p.amount) || 0) - (Number(p.surcharge_amount) || 0) - (Number(p.tip_amount) || 0)), 0);
}

/**
 * Build the business-wide Accounts Receivable: open balances grouped by customer
 * with aging. Merges new-system invoices (allocatable) with legacy completed
 * bookings that have no invoice (read-only) so the total A/R is complete.
 *
 * THE single A/R engine — the invoices page, the dashboard, and statements all
 * flow through here so their numbers can never drift apart.
 *
 * When `legacyBookings` is omitted, the completed-bookings list is fetched
 * internally; callers that already loaded bookings (Invoices page) can pass
 * theirs to skip the extra query.
 */
export async function fetchAccountsReceivable(
  businessId: string,
  legacyBookings?: any[] | null,
): Promise<AccountsReceivable> {
  // ALL new-system invoices (not just open ones): fully paid invoices must
  // still exclude their booking from the legacy fallback below, otherwise a
  // booking whose invoice was paid via customer-level allocations re-appears
  // as a phantom "legacy" A/R row at the raw booking total (which ignores
  // line discounts).
  const { data: invRows, error } = await supabase
    .from('invoices_ledger')
    .select('id, booking_id, booking_number, customer_id, customer_name, invoice_number, total_cents, amount_due_cents, due_at, invoice_date, is_overdue, booking_status, booking_deleted, status')
    .eq('business_id', businessId)
    .neq('status', 'void');
  if (error) throw error;

  let legacy = legacyBookings;
  if (legacy == null) {
    const { data: bookingRows, error: bookingsError } = await supabase
      .from('bookings')
      .select(
        'id, booking_number, total_amount, end_date, created_at, customer:customers!bookings_customer_id_fkey(id, first_name, last_name), payments(amount, surcharge_amount, tip_amount, status)',
      )
      .eq('business_id', businessId)
      .eq('status', 'completed')
      .eq('is_deleted', false);
    if (bookingsError) throw bookingsError;
    legacy = bookingRows || [];
  }

  const bookingIdsWithInvoice = new Set<string>();
  const open: { customerId: string | null; customerName: string; inv: ArOpenInvoice }[] = [];

  for (const r of (invRows || []) as any[]) {
    if (r.booking_deleted) continue;
    if (r.booking_id) bookingIdsWithInvoice.add(r.booking_id);
    // Booking invoices count once the job is completed (hides the draft every
    // active booking carries). Non-booking invoices (orders, rentals, counter
    // sales, standalone) have no booking_status — they count unless draft.
    if (r.booking_id ? r.booking_status !== 'completed' : r.status === 'draft') continue;
    if ((r.amount_due_cents || 0) <= 0) continue;
    const dueOrDate = r.due_at || r.invoice_date;
    const days = dueOrDate ? getCalendarDaysPastDue(dueOrDate) : 0;
    open.push({
      customerId: r.customer_id,
      customerName: r.customer_name || 'Unknown Customer',
      inv: {
        id: r.id,
        kind: 'invoice',
        number: r.invoice_number || (r.booking_number != null ? String(r.booking_number) : '—'),
        bookingId: r.booking_id,
        total: (r.total_cents || 0) / 100,
        balance: (r.amount_due_cents || 0) / 100,
        dueDate: r.due_at,
        invoiceDate: r.invoice_date,
        daysPastDue: Math.max(0, days),
        isOverdue: !!r.is_overdue,
      },
    });
  }

  // Legacy completed bookings without a new invoice → read-only A/R rows.
  for (const b of legacy || []) {
    if (bookingIdsWithInvoice.has(b.id)) continue;
    const total = Number(b.total_amount) || 0;
    const balance = Math.max(0, Math.round((total - principalPaid(b.payments)) * 100) / 100);
    if (balance <= 0) continue;
    const dateStr = b.end_date || b.created_at;
    const days = dateStr ? getCalendarDaysPastDue(dateStr) : 0;
    open.push({
      customerId: b.customer?.id ?? null,
      customerName: b.customer ? `${b.customer.first_name || ''} ${b.customer.last_name || ''}`.trim() || 'Unknown Customer' : 'Unknown Customer',
      inv: {
        id: b.id,
        kind: 'legacy_booking',
        number: b.booking_number != null ? String(b.booking_number) : '—',
        bookingId: b.id,
        total,
        balance,
        dueDate: null,
        invoiceDate: dateStr,
        daysPastDue: Math.max(0, days),
        isOverdue: days > 0,
      },
    });
  }

  // Group by customer + roll up aging.
  const byCustomer = new Map<string, ArCustomerGroup>();
  const buckets: AgingBucket[] = BUCKET_DEFS.map((d) => ({ ...d, amount: 0, count: 0 }));
  let totalOutstanding = 0;
  let totalOverdue = 0;
  let invoiceCount = 0;

  for (const { customerId, customerName, inv } of open) {
    const key = customerId || `name:${customerName}`;
    let group = byCustomer.get(key);
    if (!group) {
      group = { customerId, customerName, outstanding: 0, overdue: 0, invoiceCount: 0, oldestDaysPastDue: 0, invoices: [] };
      byCustomer.set(key, group);
    }
    group.invoices.push(inv);
    group.outstanding += inv.balance;
    group.invoiceCount += 1;
    if (inv.isOverdue) group.overdue += inv.balance;
    group.oldestDaysPastDue = Math.max(group.oldestDaysPastDue, inv.daysPastDue);

    totalOutstanding += inv.balance;
    invoiceCount += 1;
    if (inv.isOverdue) totalOverdue += inv.balance;
    const bk = buckets.find((x) => x.key === bucketFor(inv.daysPastDue))!;
    bk.amount += inv.balance;
    bk.count += 1;
  }

  const customers = Array.from(byCustomer.values())
    .map((g) => ({
      ...g,
      outstanding: Math.round(g.outstanding * 100) / 100,
      overdue: Math.round(g.overdue * 100) / 100,
      invoices: g.invoices.sort((a, b) => b.daysPastDue - a.daysPastDue),
    }))
    .sort((a, b) => b.outstanding - a.outstanding);

  return {
    totalOutstanding: Math.round(totalOutstanding * 100) / 100,
    totalOverdue: Math.round(totalOverdue * 100) / 100,
    customerCount: customers.length,
    invoiceCount,
    customers,
    buckets: buckets.map((b) => ({ ...b, amount: Math.round(b.amount * 100) / 100 })),
  };
}

export interface ReceivePaymentAllocation {
  invoiceId: string;
  amountCents: number;
}

/**
 * Record one customer-level payment and allocate it across invoices. Creates a
 * single payment row (no booking_id) + a payment_allocation per invoice; the
 * allocation triggers recompute each invoice's paid/balance/status.
 */
export async function receiveCustomerPayment(params: {
  businessId: string;
  customerId: string;
  method: 'cash' | 'credit_card' | 'debit_card' | 'bank_transfer' | 'check' | 'other';
  reference?: string | null;
  notes?: string | null;
  /** Full amount received. If > Σ allocations, the remainder becomes customer credit. */
  amountReceivedCents?: number;
  allocations: ReceivePaymentAllocation[];
}): Promise<{ paymentId: string }> {
  const allocs = params.allocations.filter((a) => a.amountCents > 0);
  const allocatedCents = allocs.reduce((s, a) => s + a.amountCents, 0);
  // The payment records the FULL amount received; allocations are the applied
  // portion; any remainder stays unallocated = customer credit (QuickBooks model).
  const receivedCents = params.amountReceivedCents != null ? Math.round(params.amountReceivedCents) : allocatedCents;
  if (receivedCents <= 0) throw new Error('Enter an amount to receive.');
  if (allocatedCents > receivedCents) throw new Error('Applied amount exceeds the amount received.');
  const amount = receivedCents / 100;
  const creditCents = receivedCents - allocatedCents;

  const { data: payment, error: payErr } = await supabase
    .from('payments')
    .insert({
      business_id: params.businessId,
      customer_id: params.customerId,
      booking_id: null,
      amount,
      base_amount: amount,
      surcharge_amount: 0,
      tip_amount: 0,
      method: params.method,
      status: 'completed',
      reference: params.reference ?? null,
      notes: params.notes ?? (creditCents > 0
        ? `Customer payment — ${allocs.length} invoice${allocs.length === 1 ? '' : 's'} applied, $${(creditCents / 100).toFixed(2)} credit`
        : `Customer payment applied to ${allocs.length} invoice${allocs.length === 1 ? '' : 's'}`),
      payment_type: 'partial',
    })
    .select('id')
    .single();
  if (payErr || !payment) throw payErr || new Error('Failed to create payment');

  if (allocs.length > 0) {
    const { error: allocErr } = await supabase
      .from('payment_allocations')
      .insert(allocs.map((a) => ({
        payment_id: payment.id,
        invoice_id: a.invoiceId,
        business_id: params.businessId,
        amount_cents: Math.round(a.amountCents),
      })));
    if (allocErr) throw allocErr;
  }

  return { paymentId: payment.id };
}

/** A customer's available (unapplied) credit, in cents. */
export async function fetchCustomerCredit(businessId: string, customerId: string): Promise<number> {
  const { data, error } = await supabase.rpc('get_customer_credit_cents', { p_business: businessId, p_customer: customerId });
  if (error) return 0;
  return Number(data) || 0;
}

/**
 * Apply a customer's available credit to their open invoices, oldest first.
 * Draws from the customer's unallocated payment remainders (FIFO) and creates
 * payment_allocations to the invoices — no new money moves.
 */
export async function applyCustomerCredit(businessId: string, customerId: string): Promise<{ appliedCents: number }> {
  // Credit sources: the customer's payments with unallocated principal, oldest first.
  const { data: payments, error: payErr } = await supabase
    .from('payments')
    .select('id, amount, surcharge_amount, tip_amount, created_at')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .eq('status', 'completed')
    .order('created_at', { ascending: true });
  if (payErr) throw payErr;

  const sources: { paymentId: string; remainder: number }[] = [];
  for (const p of payments || []) {
    const principal = Math.max(0, Math.round(((Number(p.amount) || 0) - (Number(p.surcharge_amount) || 0) - (Number(p.tip_amount) || 0)) * 100));
    const { data: allocs } = await supabase.from('payment_allocations').select('amount_cents').eq('payment_id', p.id);
    const allocated = (allocs || []).reduce((s: number, a: any) => s + (a.amount_cents || 0), 0);
    const remainder = principal - allocated;
    if (remainder > 0) sources.push({ paymentId: p.id, remainder });
  }
  if (sources.length === 0) return { appliedCents: 0 };

  const invoices = await fetchCustomerOpenInvoices(businessId, customerId); // oldest first
  const inserts: { payment_id: string; invoice_id: string; business_id: string; amount_cents: number }[] = [];
  let si = 0;
  for (const inv of invoices as any[]) {
    let need = inv.amount_due_cents || 0;
    while (need > 0 && si < sources.length) {
      const src = sources[si];
      if (src.remainder <= 0) { si++; continue; }
      const take = Math.min(need, src.remainder);
      inserts.push({ payment_id: src.paymentId, invoice_id: inv.id, business_id: businessId, amount_cents: take });
      src.remainder -= take;
      need -= take;
      if (src.remainder <= 0) si++;
    }
    if (si >= sources.length) break;
  }

  if (inserts.length > 0) {
    const { error } = await supabase.from('payment_allocations').insert(inserts);
    if (error) throw error;
  }
  return { appliedCents: inserts.reduce((s, x) => s + x.amount_cents, 0) };
}

/**
 * Backfill any of this customer's legacy completed bookings (no invoice row)
 * into the invoice system so they become allocatable. Best-effort: failures
 * are ignored and the booking simply stays a read-only legacy A/R row.
 */
async function backfillCustomerLegacyBookings(businessId: string, customerId: string): Promise<void> {
  const { data: bookings } = await supabase
    .from('bookings')
    .select('id')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .eq('status', 'completed')
    .eq('is_deleted', false);
  const ids = (bookings || []).map((b: any) => b.id);
  if (ids.length === 0) return;

  const { data: invs } = await supabase
    .from('invoices')
    .select('booking_id')
    .eq('business_id', businessId)
    .in('booking_id', ids);
  const have = new Set((invs || []).map((r: any) => r.booking_id));
  const missing = ids.filter((id) => !have.has(id));

  for (const id of missing) {
    const { error } = await supabase.rpc('backfill_booking_invoice', { p_booking_id: id });
    if (error) console.warn('backfill_booking_invoice failed for booking', id, error.message);
  }
}

/** Open (allocatable) new-system invoices for one customer, oldest first — used by Receive Payment. */
export async function fetchCustomerOpenInvoices(businessId: string, customerId: string) {
  // Legacy completed bookings have no invoice row, so Receive Payment couldn't
  // see them — backfill them into the invoice system first (idempotent).
  await backfillCustomerLegacyBookings(businessId, customerId);

  const { data, error } = await supabase
    .from('invoices_ledger')
    .select('*')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .gt('amount_due_cents', 0)
    .neq('status', 'void')
    // Booking invoices once completed; non-booking invoices (orders, rentals,
    // counter sales, standalone) once issued.
    .or('and(booking_id.not.is.null,booking_status.eq.completed),and(booking_id.is.null,status.neq.draft)')
    .order('invoice_date', { ascending: true });
  if (error) throw error;
  return data || [];
}

/**
 * Every meaningful invoice for a customer (paid, partial, unpaid, void) for
 * the Billing tab history: final booking invoices (booking completed) plus
 * issued standalone invoices. Drafts of not-yet-completed bookings are noise
 * (every booking carries one) and stay hidden.
 */
export async function fetchCustomerAllInvoices(businessId: string, customerId: string) {
  const { data, error } = await supabase
    .from('invoices_ledger')
    .select('*')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .or('and(booking_id.not.is.null,booking_status.eq.completed),and(booking_id.is.null,status.neq.draft)')
    .order('invoice_date', { ascending: false });
  if (error) throw error;
  return (data || []).filter((row: { booking_deleted?: boolean | null }) => !row.booking_deleted);
}
